import { useState, useCallback, useEffect, useMemo, useRef, type MutableRefObject } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/hooks/useAuth';
import { useBuilderSession, VerboseEvent } from '../hooks/useBuilderSession';
import { useArcheReviews } from '../hooks/useArcheReview';
import { ConversationPane } from '../components/ConversationPane';
import { PreviewPane } from '../components/PreviewPane';
import { PromptInput } from '../components/PromptInput';
import { BuilderHeader } from '../components/BuilderHeader';
import { ReviewStatus } from '../components/ReviewStatus';
import { ReportProblemDialog } from '../components/ReportProblemDialog';
import { ModeTabBar } from '../components/guided/ModeTabBar';
import { GuidedWizard } from '../components/guided/GuidedWizard';
import { api } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import type { BuilderMode, GuidedPhase, GuidedFlow, BuildPlan } from '../types/guided-builder';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export function BuilderPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = useMemo(() => user?.roles?.includes('admin') ?? false, [user?.roles]);

  const [suggestedNextSteps, setSuggestedNextSteps] = useState<string[]>([]);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [verbose, setVerbose] = useState(false);
  const [deepThinking, setDeepThinking] = useState(false);
  const logEntriesRef = useRef<unknown[]>([]);

  // Guided builder state
  const [builderMode, setBuilderMode] = useState<BuilderMode>('guided');
  const [autoApprove, setAutoApprove] = useState(false);
  const autoApproveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevIsSendingForAutoApproveRef = useRef(false);
  // Lifted input state for PromptInput (rendered in two DOM positions)
  const [inputValue, setInputValue] = useState('');

  // Verbose event persistence: committed groups keyed by assistant message timestamp
  const [verboseHistory, setVerboseHistory] = useState<Record<string, VerboseEvent[]>>({});
  // In-progress events for the current response
  const [pendingVerboseEvents, setPendingVerboseEvents] = useState<VerboseEvent[]>([]);
  const pendingVerboseRef = useRef<VerboseEvent[]>([]);
  const prevIsSendingRef = useRef(false);
  const flushRafRef = useRef<number | null>(null) as MutableRefObject<number | null>;

  const handleVerboseEvent = useCallback((event: VerboseEvent) => {
    // model_info marks the start of a new response — start a fresh pending batch
    if (event.type === 'model_info') {
      pendingVerboseRef.current = [event];
    } else {
      pendingVerboseRef.current = [...pendingVerboseRef.current, event];
    }
    // Batch updates: coalesce multiple events into a single state update per animation frame
    if (flushRafRef.current === null) {
      flushRafRef.current = requestAnimationFrame(() => {
        setPendingVerboseEvents([...pendingVerboseRef.current]);
        flushRafRef.current = null;
      });
    }
  }, []);

  // Track the derived guided phase in a ref for use by useBuilderSession
  // (avoids circular dependency: session -> guidedPhase -> useBuilderSession -> session)
  const derivedGuidedPhaseRef = useRef<string | undefined>(undefined);

  const {
    session,
    preview,
    isLoading,
    isSending,
    isSaving,
    isResetting,
    error,
    streamingText,
    sendMessage,
    stopGeneration,
    saveSession,
    updateSettings,
    resetContext,
    undo,
    redo,
    canUndo,
    canRedo,
    undoCount,
    redoCount,
    uploadPreviewScreenshot,
  } = useBuilderSession({
    sessionId,
    verbose,
    useOpus: deepThinking,
    guidedPhase: derivedGuidedPhaseRef.current,
    onError: (err) => {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    },
    onVerboseEvent: handleVerboseEvent,
  });

  // Derive guided flow state from session
  // currentSettings is typed as unknown - cast to Record for property access
  const settingsObj = session?.currentSettings as Record<string, unknown> | null | undefined;
  const guidedFlow = settingsObj?.guidedFlow as GuidedFlow | undefined;
  const buildPlan = settingsObj?.buildPlan as BuildPlan | undefined;
  const guidedPhase: GuidedPhase = guidedFlow?.phase ?? 'ideation';
  const isGuidedMode = builderMode === 'guided';

  // Keep ref updated so useBuilderSession reads the latest phase
  derivedGuidedPhaseRef.current = isGuidedMode ? guidedPhase : undefined;

  // Determine initial builder mode from session state
  useEffect(() => {
    if (session) {
      const hasExistingArche = !!session.archId && !!session.currentUiDefinition;
      const flow = (session.currentSettings as Record<string, unknown> | null)?.guidedFlow as
        | GuidedFlow
        | undefined;
      const plan = (session.currentSettings as Record<string, unknown> | null)?.buildPlan as
        | BuildPlan
        | undefined;

      if (flow) {
        // If the guided build is complete, or execution finished but phase
        // wasn't transitioned (pre-fix sessions), go straight to chat mode
        const allStepsVerified =
          plan?.approved &&
          plan.steps.length > 0 &&
          plan.steps.every((s) => s.status === 'verified');
        if (flow.phase === 'complete' || (flow.phase === 'execution' && allStepsVerified)) {
          setBuilderMode('chat');
        } else {
          setBuilderMode('guided');
        }
      } else if (hasExistingArche) {
        setBuilderMode('chat');
      }
    }
  }, [session?.archId, session?.currentUiDefinition, settingsObj?.guidedFlow]);

  const {
    reviews,
    isLoading: reviewsLoading,
    requestReview,
  } = useArcheReviews({
    archId: session?.archId || '',
    pollInterval: 10000,
  });

  // Keep a ref to messages for use in the commit effect
  const messagesRef = useRef(session?.conversationHistory || []);
  messagesRef.current = session?.conversationHistory || [];

  // Commit pending verbose events when isSending transitions from true to false
  useEffect(() => {
    if (prevIsSendingRef.current && !isSending) {
      const events = pendingVerboseRef.current;
      if (events.length > 0) {
        const msgs = messagesRef.current;
        const lastMsg = msgs[msgs.length - 1];
        if (lastMsg && lastMsg.role === 'assistant') {
          setVerboseHistory((prev) => ({
            ...prev,
            [lastMsg.timestamp]: events,
          }));
        }
      }
      pendingVerboseRef.current = [];
      setPendingVerboseEvents([]);
    }
    prevIsSendingRef.current = isSending;
  }, [isSending]);

  const handleSendMessage = useCallback(
    async (message: string) => {
      const response = await sendMessage(message);
      if (response) {
        setSuggestedNextSteps(response.suggestedNextSteps || []);
      }
    },
    [sendMessage],
  );

  const handlePromptSubmit = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || isSending) return;
    setInputValue('');
    handleSendMessage(trimmed);
  }, [inputValue, isSending, handleSendMessage]);

  const handleSave = useCallback(
    async (name: string, description: string) => {
      const result = await saveSession(name, description);
      if (result) {
        toast({
          title: 'Saved',
          description: `Arche saved as draft (${result.archId})`,
        });
      }
    },
    [saveSession, toast],
  );

  const handleRequestReview = useCallback(async () => {
    if (!session?.archId) {
      toast({
        title: 'Save first',
        description: 'Please save your Arche before requesting a review.',
        variant: 'destructive',
      });
      return;
    }

    const result = await requestReview();
    if (result) {
      toast({
        title: 'Review Requested',
        description: `Review started. Estimated cost: ${(result.estimatedCostCents / 100).toFixed(2)} credits`,
      });
      setReviewDialogOpen(false);
    }
  }, [session?.archId, requestReview, toast]);

  const handleSettingsChange = useCallback(
    (settings: unknown) => {
      updateSettings(settings);
    },
    [updateSettings],
  );

  const handleResetContext = useCallback(async () => {
    await resetContext();
    setSuggestedNextSteps([]);
    setVerboseHistory({});
    setPendingVerboseEvents([]);
    pendingVerboseRef.current = [];
    if (flushRafRef.current !== null) {
      cancelAnimationFrame(flushRafRef.current);
      flushRafRef.current = null;
    }
    toast({
      title: 'Context Reset',
      description: 'Conversation history cleared. Your UI preview has been preserved.',
    });
  }, [resetContext, toast]);

  // Guided builder callbacks
  const handleGuidedStart = useCallback(
    async (idea: string, name?: string, description?: string) => {
      if (!sessionId) return;
      try {
        // Set the arche name and description if provided
        const settings: Record<string, string> = {};
        if (name) settings.name = name;
        if (description) settings.description = description;
        if (Object.keys(settings).length > 0) {
          await updateSettings(settings);
        }
        await api.startGuidedFlow(sessionId, idea);
        // Send the idea as a message to trigger AI clarifying questions
        await sendMessage(idea);
      } catch (err) {
        toast({
          title: 'Error',
          description: (err as Error).message,
          variant: 'destructive',
        });
      }
    },
    [sessionId, sendMessage, updateSettings, toast],
  );

  const handleSubmitAnswers = useCallback(
    async (answers: Record<number, string>) => {
      const qaList = guidedFlow?.clarifyingQA ?? [];
      const lines = Object.entries(answers)
        .map(([indexStr, answer]) => {
          const idx = Number(indexStr);
          const question = qaList[idx]?.question ?? `Question ${idx + 1}`;
          return `Q: ${question}\nA: ${answer}`;
        })
        .join('\n\n');
      // The planning panel will show streaming text, so keep panel view.
      // The auto-switch effect will handle the transition when phase changes.
      // Send answers with 'planning' phase override so AI gets planning tools/prompt
      // and creates the build plan in the same turn
      await sendMessage(
        `Here are my answers:\n\n${lines}\n\nPlease create a build plan based on my idea and these answers.`,
        { guidedPhaseOverride: 'planning' },
      );
    },
    [sendMessage, guidedFlow?.clarifyingQA],
  );

  const formatAssumptionComments = useCallback(
    (comments: Record<string, string>) => {
      const assumptions = guidedFlow?.assumptions ?? [];
      return Object.entries(comments)
        .map(([id, feedback]) => {
          const assumption = assumptions.find((a) => a.id === id);
          const text = assumption?.assumption ?? id;
          return `- "${text}" — ${feedback}`;
        })
        .join('\n');
    },
    [guidedFlow?.assumptions],
  );

  const handleSuggestAssumptionChanges = useCallback(
    async (comments: Record<string, string>) => {
      const commentLines = formatAssumptionComments(comments);
      await sendMessage(
        `I have feedback on some of your assumptions:\n${commentLines}\n\nPlease revise the plan accordingly.`,
      );
    },
    [sendMessage, formatAssumptionComments],
  );

  const handlePlanReviewApprove = useCallback(async () => {
    // AssumptionReview (Step 3) will show a loading state while Claude
    // generates assumptions, then populate when they arrive.
    await sendMessage('I approve this build plan. Please set the assumptions and proceed.', {
      guidedPhaseOverride: 'assumptions',
    });
  }, [sendMessage]);

  const handlePlanReviewChanges = useCallback(
    async (feedback: string) => {
      await sendMessage(
        `I'd like some changes to the build plan:\n\n${feedback}\n\nPlease update the plan accordingly.`,
      );
    },
    [sendMessage],
  );

  const handleApprovePlan = useCallback(
    async (comments?: Record<string, string>) => {
      if (comments && Object.keys(comments).length > 0) {
        const commentLines = formatAssumptionComments(comments);
        await sendMessage(
          `I have a few notes on the assumptions, but overall the plan looks good. Please incorporate this feedback, approve the plan, and start building:\n${commentLines}`,
        );
      } else {
        await sendMessage('The plan looks good. Please approve it and start building.');
      }
    },
    [sendMessage, formatAssumptionComments],
  );

  const handleExecutionFeedback = useCallback(
    async (message: string) => {
      await sendMessage(message);
    },
    [sendMessage],
  );

  // Auto-approve effect: when auto-approve is ON and AI finishes a step
  // Uses its own ref to avoid racing with the verbose commit effect
  useEffect(() => {
    const wasSending = prevIsSendingForAutoApproveRef.current;
    prevIsSendingForAutoApproveRef.current = isSending;

    if (!autoApprove || !isGuidedMode || guidedPhase !== 'execution') return;

    // Watch isSending transition true -> false
    if (!isSending && wasSending) {
      autoApproveTimerRef.current = setTimeout(() => {
        sendMessage(
          'Continue. If the current step is fully built, verify and advance to the next step.',
        );
      }, 2000);
    }

    return () => {
      if (autoApproveTimerRef.current) {
        clearTimeout(autoApproveTimerRef.current);
        autoApproveTimerRef.current = null;
      }
    };
  }, [isSending, autoApprove, isGuidedMode, guidedPhase, sendMessage]);

  // Dynamic placeholder for guided phases
  const guidedPlaceholder = useMemo(() => {
    if (!isGuidedMode) return undefined;
    switch (guidedPhase) {
      case 'ideation':
        return 'Describe the app you want to build...';
      case 'clarification':
        return "Answer the AI's questions...";
      case 'planning':
        return 'Review the plan or request changes...';
      case 'execution':
        return autoApprove ? 'Auto-approving steps...' : 'Give feedback or continue...';
      case 'complete':
        return 'Continue chatting to refine your app...';
      default:
        return undefined;
    }
  }, [isGuidedMode, guidedPhase, autoApprove]);

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (flushRafRef.current !== null) {
        cancelAnimationFrame(flushRafRef.current);
      }
      if (autoApproveTimerRef.current) {
        clearTimeout(autoApproveTimerRef.current);
      }
    };
  }, []);

  // Track which pane was last clicked for scoped Ctrl+A selection
  const conversationPaneRef = useRef<HTMLDivElement>(null);
  const previewPaneRef = useRef<HTMLDivElement>(null);
  const activePaneRef = useRef<HTMLDivElement | null>(null);

  const handlePaneFocus = useCallback((paneRef: React.RefObject<HTMLDivElement | null>) => {
    activePaneRef.current = paneRef.current;
  }, []);

  // Mobile horizontal scroll tracking (same pattern as MultiAIPage)
  const panelsContainerRef = useRef<HTMLDivElement>(null);
  const scrollTrackRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const snapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [scrollState, setScrollState] = useState({ scrollLeft: 0, scrollWidth: 0, clientWidth: 0 });

  useEffect(() => {
    const container = panelsContainerRef.current;
    if (!container) return;

    const updateScrollState = () => {
      setScrollState({
        scrollLeft: container.scrollLeft,
        scrollWidth: container.scrollWidth,
        clientWidth: container.clientWidth,
      });
    };

    // Snap to nearest panel after scrolling stops (only for finger swipes, not slider drags)
    const scheduleSnap = () => {
      if (isDraggingRef.current) return;
      if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
      snapTimeoutRef.current = setTimeout(() => {
        const maxScroll = container.scrollWidth - container.clientWidth;
        if (maxScroll <= 0) return;
        const ratio = container.scrollLeft / maxScroll;
        const target = ratio > 0.5 ? maxScroll : 0;
        container.scrollTo({ left: target, behavior: 'smooth' });
      }, 150);
    };

    const handleScroll = () => {
      updateScrollState();
      scheduleSnap();
    };

    updateScrollState();

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateScrollState);
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateScrollState);
      resizeObserver.disconnect();
      if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
    };
  }, [isLoading]); // Re-run after loading finishes and the scroll container mounts

  const handleScrollbarDrag = useCallback((clientX: number) => {
    const container = panelsContainerRef.current;
    const track = scrollTrackRef.current;
    if (!container || !track) return;

    const trackRect = track.getBoundingClientRect();
    const trackWidth = trackRect.width;
    const thumbWidth = 24;
    const maxThumbLeft = trackWidth - thumbWidth;

    const relativeX = clientX - trackRect.left - thumbWidth / 2;
    const clampedX = Math.max(0, Math.min(relativeX, maxThumbLeft));

    const scrollRatio = clampedX / maxThumbLeft;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    container.scrollLeft = scrollRatio * maxScrollLeft;
  }, []);

  const scrollToPanel = useCallback((panel: 'left' | 'right') => {
    const container = panelsContainerRef.current;
    if (!container) return;
    const target = panel === 'left' ? 0 : container.scrollWidth - container.clientWidth;
    container.scrollTo({ left: target, behavior: 'smooth' });
  }, []);

  // Keyboard shortcuts for undo/redo and scoped Ctrl+A
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      // Ctrl/Cmd + A: scope selection to the active pane
      if (modifier && e.key.toLowerCase() === 'a') {
        const target = e.target as HTMLElement;
        // Let inputs/textareas handle their own Ctrl+A
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          return;
        }

        if (activePaneRef.current) {
          e.preventDefault();
          const selection = window.getSelection();
          if (selection) {
            const range = document.createRange();
            range.selectNodeContents(activePaneRef.current);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }

      // Check for Ctrl/Cmd + Z (undo) or Ctrl/Cmd + Shift + Z (redo)
      if (modifier && e.key.toLowerCase() === 'z') {
        // Don't trigger if user is typing in an input
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          return;
        }

        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }

      // Also support Ctrl/Cmd + Y for redo (Windows convention)
      if (modifier && e.key.toLowerCase() === 'y') {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          return;
        }

        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  if (!sessionId) {
    navigate('/a/arche_builder');
    return null;
  }

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && !session) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-destructive">Failed to load session</p>
        <Button onClick={() => navigate('/a/arche_builder')}>Back to Sessions</Button>
      </div>
    );
  }

  const messages = session?.conversationHistory || [];
  const hasMessages = messages.length > 0;

  const promptInputProps = {
    value: inputValue,
    onChange: setInputValue,
    onSubmit: handlePromptSubmit,
    onStopGeneration: stopGeneration,
    onResetContext: handleResetContext,
    isProcessing: isSending,
    isResetting,
    hasMessages,
    placeholder: guidedPlaceholder,
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <BuilderHeader
        session={session}
        onSave={handleSave}
        onRequestReview={() => setReviewDialogOpen(true)}
        onReportProblem={() => setReportDialogOpen(true)}
        onSettingsChange={handleSettingsChange}
        isSaving={isSaving}
      />

      {/* Main content wrapper */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Scroll container — ref must be on the element that actually scrolls */}
        <div
          ref={panelsContainerRef}
          className="flex-1 min-h-0 overflow-x-auto md:overflow-x-hidden overflow-y-hidden scrollbar-hide"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {/* Inner row — width forces overflow on mobile */}
          <div className="h-full flex flex-row w-[200vw] md:w-full">
            {/* Left: Mode tabs + guided wizard or chat */}
            <div
              ref={conversationPaneRef}
              onMouseDown={() => handlePaneFocus(conversationPaneRef)}
              className="w-[100vw] md:w-1/2 border-r flex flex-col min-h-0"
            >
              {/* Mode tab bar — always visible at top of left pane */}
              <ModeTabBar mode={builderMode} onModeChange={setBuilderMode} />

              {/* Guided mode: wizard */}
              {isGuidedMode ? (
                <GuidedWizard
                  guidedPhase={guidedPhase}
                  guidedFlow={guidedFlow}
                  buildPlan={buildPlan}
                  messages={messages}
                  isSending={isSending}
                  streamingText={streamingText}
                  suggestedNextSteps={suggestedNextSteps}
                  verbose={verbose}
                  verboseHistory={verboseHistory}
                  pendingVerboseEvents={pendingVerboseEvents}
                  onGuidedStart={handleGuidedStart}
                  onBrowseTemplates={() => navigate('/a/arche_builder/templates')}
                  onSubmitAnswers={handleSubmitAnswers}
                  onPlanReviewApprove={handlePlanReviewApprove}
                  onPlanReviewChanges={handlePlanReviewChanges}
                  onSuggestAssumptionChanges={handleSuggestAssumptionChanges}
                  onApprovePlan={handleApprovePlan}
                  onExecutionFeedback={handleExecutionFeedback}
                  onStopGeneration={stopGeneration}
                  onContinueChat={() => setBuilderMode('chat')}
                  onSuggestionClick={setInputValue}
                  autoApprove={autoApprove}
                  onAutoApproveChange={setAutoApprove}
                  onSendMessage={handleSendMessage}
                />
              ) : (
                <>
                  <ConversationPane
                    messages={messages}
                    isProcessing={isSending}
                    suggestedNextSteps={suggestedNextSteps}
                    streamingText={streamingText}
                    verboseHistory={verbose ? verboseHistory : undefined}
                    pendingVerboseEvents={verbose ? pendingVerboseEvents : undefined}
                    onSuggestionClick={setInputValue}
                    className="flex-1 min-h-0"
                  />
                  {/* Desktop-only prompt input */}
                  <div className="hidden md:block">
                    <PromptInput {...promptInputProps} />
                  </div>
                </>
              )}
            </div>

            {/* Right: Preview and Review */}
            <div
              ref={previewPaneRef}
              onMouseDown={() => handlePaneFocus(previewPaneRef)}
              className="w-[100vw] md:w-1/2 flex flex-col min-h-0"
            >
              <PreviewPane
                uiDefinition={session?.currentUiDefinition || preview}
                className="flex-1 min-h-0"
                onUndo={undo}
                onRedo={redo}
                canUndo={canUndo}
                canRedo={canRedo}
                undoCount={undoCount}
                redoCount={redoCount}
                verbose={verbose}
                onVerboseChange={setVerbose}
                deepThinking={deepThinking}
                onDeepThinkingChange={setDeepThinking}
                isAdmin={isAdmin}
                logEntriesRef={logEntriesRef}
                onScreenshotCaptured={uploadPreviewScreenshot}
                archeId={session?.archId || undefined}
                processGraph={buildPlan?.graph}
                currentNodeId={buildPlan?.currentNodeId}
              />

              {/* Review status */}
              {session?.archId && (
                <div className="border-t p-4">
                  <h3 className="text-sm font-medium mb-2">Review Status</h3>
                  <ReviewStatus reviews={reviews} isLoading={reviewsLoading} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile-only slider with nav buttons */}
        <div className="md:hidden flex-shrink-0 border-t border-border bg-muted/30 px-10 py-2 flex items-center gap-1">
          <button
            onClick={() => scrollToPanel('left')}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-muted-foreground" />
          </button>

          {/* Track — ~70% width */}
          <div className="flex-1 max-w-[70%] mx-auto">
            <div
              ref={scrollTrackRef}
              className="relative h-6 cursor-pointer flex items-center"
              onMouseDown={(e) => {
                e.preventDefault();
                isDraggingRef.current = true;
                handleScrollbarDrag(e.clientX);

                const handleMouseMove = (moveEvent: MouseEvent) => {
                  handleScrollbarDrag(moveEvent.clientX);
                };
                const handleMouseUp = () => {
                  isDraggingRef.current = false;
                  document.removeEventListener('mousemove', handleMouseMove);
                  document.removeEventListener('mouseup', handleMouseUp);
                };
                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                isDraggingRef.current = true;
                const touch = e.touches[0];
                handleScrollbarDrag(touch.clientX);

                const handleTouchMove = (moveEvent: TouchEvent) => {
                  const moveTouch = moveEvent.touches[0];
                  handleScrollbarDrag(moveTouch.clientX);
                };
                const handleTouchEnd = () => {
                  isDraggingRef.current = false;
                  document.removeEventListener('touchmove', handleTouchMove);
                  document.removeEventListener('touchend', handleTouchEnd);
                };
                document.addEventListener('touchmove', handleTouchMove, { passive: false });
                document.addEventListener('touchend', handleTouchEnd);
              }}
            >
              {/* Thin track line */}
              <div className="absolute inset-x-0 h-1 bg-muted rounded-full" />
              {/* Circular thumb */}
              <div
                className="absolute w-6 h-6 bg-primary/80 hover:bg-primary rounded-full transition-colors shadow-sm"
                style={{
                  left: (() => {
                    const maxScroll = scrollState.scrollWidth - scrollState.clientWidth;
                    if (maxScroll <= 0) return '0px';
                    const ratio = scrollState.scrollLeft / maxScroll;
                    return `calc(${ratio * 100}% - ${ratio * 24}px)`;
                  })(),
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              />
            </div>
          </div>

          <button
            onClick={() => scrollToPanel('right')}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
          >
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Mobile-only: prompt input (chat mode only) */}
        {!isGuidedMode && (
          <div className="md:hidden">
            <PromptInput {...promptInputProps} />
          </div>
        )}
      </div>

      {/* Report Problem Dialog */}
      <ReportProblemDialog
        open={reportDialogOpen}
        onOpenChange={setReportDialogOpen}
        session={session}
        logEntries={logEntriesRef.current}
        guidedBuilderState={{ mode: builderMode, phase: guidedPhase, autoApprove }}
      />

      {/* Request Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Review</DialogTitle>
            <DialogDescription>
              Submit your Arche for AI-powered review. This will check for security issues, policy
              compliance, and quality.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              The review process typically takes 1-2 minutes. You&apos;ll be notified when it&apos;s
              complete.
            </p>
            <p className="text-sm font-medium">
              Publishing fee: $10.00 (charged when approved and listed)
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRequestReview}>Start Review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
